import { useInject } from 'dependable-react';
import { css } from 'emotion';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useState } from 'react';

import { Avatar } from '../state/models/Avatar';
import { Magic } from '../state/models/Magic';
import { BREAKPOINT_LARGE, BREAKPOINT_SMALL } from '../consts/breakpoints';
import { APP_DATA_TOKEN } from '../services/InjectTokens';
import background from '../assets/preview-background.jpg';
import { useVariables } from '../hooks/useVariables';
import { usePageAvatars } from '../hooks/usePageAvatars';
import { Desktop } from './Desktop';

import { ReactComponent as IconZoom } from '../assets/magicBuilder/iconZoom.svg';
import { Mobile } from './Mobile';
import { Media } from '../containers/App';

const main = (isWideFormat: boolean) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  padding: 10px;

  background: url(${background});
  background-size: cover;
  background-repeat: no-repeat;

  height: 420px;
  overflow: hidden;

  @media (min-width: ${BREAKPOINT_SMALL}) {
    height: 480px;
  }

  @media (min-width: ${BREAKPOINT_LARGE}) {
    height: ${isWideFormat ? '500px' : '625px'};
  }
`;

const slider = css`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  height: 100%;
  width: 100%;

  overflow-x: scroll;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${BREAKPOINT_LARGE}) {
    justify-content: center;
    overflow: hidden;
  }
`;

const zoomIconStyle = css`
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 50px;
  height: 50px;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 100%;
  cursor: pointer;
  z-index: 1;
`;

interface IBookSliderProps {
  currentPage: number | null;
  avatars: Array<Avatar>;
  magicPersons?: Magic;
  resetCurrentPage(): void;
}
export const BookContext = React.createContext({});

export const Book = observer(({ currentPage, resetCurrentPage, magicPersons }: IBookSliderProps) => {
  const appState = useInject(APP_DATA_TOKEN);
  const pages = useMemo(() => appState.pageImages, []);
  const totalPages = appState.pageImages.length - 1;
  const [isZoomed, setIsZoomed] = useState(false);
  const variables = useVariables(appState.avatars, magicPersons);
  const pageAvatars = usePageAvatars(appState.avatars, pages, appState);

  const handleZoom = () => {
    setIsZoomed((prevState) => !prevState);
  };
  return (
    <BookContext.Provider
      value={{
        pageAvatars,
        totalPages,
        variables,
        contentString: appState.contentString,
        coverDimensions: appState.wrapDimensions,
        pageDimensions: appState.dimensions,
        isWideFormat: appState.isWideFormat,
      }}
    >
      <div className={main(appState.isWideFormat)}>
        <div className={slider}>
          <Media greaterThanOrEqual="lg">
            <Desktop
              pages={pages}
              page={currentPage}
              isZoomed={isZoomed}
              handleZoom={handleZoom}
              resetCurrentPage={resetCurrentPage}
            />
          </Media>
          <Media lessThan="lg">
            <Mobile
              pages={pages}
              page={currentPage}
              isZoomed={isZoomed}
              handleZoom={handleZoom}
              resetCurrentPage={resetCurrentPage}
            />
          </Media>
        </div>
        {!isZoomed && (
          <div role="button" onClick={handleZoom} className={zoomIconStyle}>
            <IconZoom />
          </div>
        )}
      </div>
    </BookContext.Provider>
  );
});
